import { MetricsTracker, USER_EVENTS, generateMetricsAttribute } from '@sm/metrics';
import { AMPLITUDE_PAGE_EVENTS, AMPLITUDE_SELECTED_PACKAGE_MAP } from '~shared/util';
import { Sources } from '~shared/constants/pricing';
import { AmplitudeProps, CTAClickEventType, PageEventLoadType } from './types';

/**
 * Function to create an amplitude event to log the pricing page being loaded
 *
 * @param {string} pageName   Name of page being loaded
 * @param {string} utSource   utSource params
 * @returns void
 */
export function logPricingPageLoad({ pageName, utSource }: PageEventLoadType): void {
  if (!pageName) {
    return;
  }

  // async this to not block main thread
  setTimeout(() => {
    MetricsTracker.track({
      name: USER_EVENTS.PAGE_VIEW,
      data: {
        actionType: USER_EVENTS.PAGE_VIEW,
        actionFlow: pageName,
        amplitudeEvent: AMPLITUDE_PAGE_EVENTS.pageView,
        pageName,
        upgradeTriggerSource: utSource,
        source: Sources.PRICINGWEB,
      },
    });
  }, 0);
}

/**
 * Function to create an amplitude event to log a CTA button being clicked
 *
 * @param {string} pageName     Name of page being loaded
 * @param {string} packageId    ID of package where CTA was clicked
 * @param {string} packageName  Name of package where CTA was clicked
 * @returns String
 */
export function generateCTAMetricsAttribute({ pageName, packageId, packageName }: CTAClickEventType): String {
  return generateMetricsAttribute({
    name: USER_EVENTS.ELEMENT_INTERACTION,
    data: {
      actionType: USER_EVENTS.ELEMENT_INTERACTION,
      actionFlow: pageName,
      amplitudeEvent: AMPLITUDE_PAGE_EVENTS.selectedPlans,
      pageName,
      selectedPackageId: packageId,
      selectedPackageName: AMPLITUDE_SELECTED_PACKAGE_MAP[packageName] ?? packageName.toLowerCase(),
      source: Sources.PRICINGWEB,
    },
  });
}

/**
 * A generic function to log amplitude events
 */
export function logAmplitudeEvent({
  eventName,
  pageName,
  amplitudeEvent = AMPLITUDE_PAGE_EVENTS.pageView,
  source = Sources.PRICINGWEB,
  userEvent = USER_EVENTS.ELEMENT_INTERACTION,
  otherData = {},
}: AmplitudeProps): void {
  // async this to not block main thread
  setTimeout(() => {
    MetricsTracker.track({
      name: userEvent,
      data: {
        pageName,
        amplitudeEvent,
        eventName,
        source,
        ...otherData,
      },
    });
  }, 0);
}
